import { ActionReducerMapBuilder, createAsyncThunk } from "@reduxjs/toolkit";
import { graphqlRequest } from "context/graphql/functions";
import { AppAsyncThunkConfig } from "store";
import { State } from "./addPart.store";
import { DeviceTestStatusEnum } from "pages/jobs/jobDetails/visit/inspection/DeviceTestStatusEnum";

export const createAppAsyncThunk = createAsyncThunk.withTypes<AppAsyncThunkConfig>();

export const asyncMutations = {
  createDevice: createAppAsyncThunk(
    "createDevice/createDevice",
    async (
      deviceData: { 
        selectedPartId: number; 
        location: string; 
        installedDate?: Date | null; 
        engineerName: string;
        engineerId: number;
        notes: string;
        testResult: DeviceTestStatusEnum | null;
        workUnitId: number;
        equipmentId: number;
      },
      { rejectWithValue, extra: { sdk } }
    ) => {
      if (
        !deviceData.selectedPartId || 
        !deviceData.location || 
        !deviceData.engineerId ||
        !deviceData.engineerName ||
        !deviceData.workUnitId
      ) {
        return rejectWithValue("Missing values when creating device");
      }

      const response = await graphqlRequest(sdk.createDevice, {
        thunkName: "createDevice/createDevice",
        variables: {
          deviceDetails: {
            partId: deviceData.selectedPartId,
            location: deviceData.location,
            installedDate: deviceData.installedDate?.toISOString() ?? null,
            testResult: deviceData.testResult !== null ? Number(deviceData.testResult) : null,
            notes: deviceData.notes,
            engineerId: deviceData.engineerId,
            engineerName: deviceData.engineerName,
            workUnitId: deviceData.workUnitId,
            equipmentId: deviceData.equipmentId
          },
        }        
      });

      const { data, errors, queued } = response;

      if (errors) {
        return rejectWithValue(errors);
      }

      if (!data?.createDevice) {
        return rejectWithValue("Something went wrong");
      }

      if (data.createDevice.partId == null) {
        return rejectWithValue("Invalid response from server");
      }

      return { queued, deviceId: data.createDevice.id };
    }
  ),

  updateLocation: createAppAsyncThunk(
    "createDevice/updateDeviceLocation",
    async (updateData: { deviceId: number, newLocation: string }, { rejectWithValue, extra: { sdk } }) => {
      const { deviceId, newLocation } = updateData;

      if (!deviceId || !newLocation) {
        return rejectWithValue("Missing eventId or location");
      }

      const response = await graphqlRequest(sdk.updateDeviceLocation, {
        thunkName: "createDevice/updateDeviceLocation",
        variables: { deviceId: deviceId, location: newLocation }
      });

      const { data, errors } = response;

      if (errors) {
        return rejectWithValue(errors);
      }

      if (!data?.updateDeviceLocation) {
        return rejectWithValue("Failed to update location");
      }

      return { deviceId, newLocation: data.updateDeviceLocation.location };
    }
  ),
};

export const updateLocation = asyncMutations.updateLocation;

export const mutationBuilder = (builder: ActionReducerMapBuilder<State>) => {
  builder.addCase(asyncMutations.createDevice.pending, (state) => {
    state.loading = true;
  });
  builder.addCase(asyncMutations.createDevice.rejected, (state) => {
    state.loading = false;
  });
  builder.addCase(asyncMutations.createDevice.fulfilled, (state) => {
    state.loading = false;
  });
};
